import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AppState, Theme, Toast } from '@/types';

interface AppStore extends AppState {
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  toggleSidebar: () => void;
  setSidebarOpen: (isOpen: boolean) => void;
  setSidebarCollapsed: (isCollapsed: boolean) => void;
  addNotification: (toast: Omit<Toast, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  setLoading: (isLoading: boolean) => void;
}

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      theme: {
        mode: 'light',
        primaryColor: '#0ea5e9',
      },
      sidebar: {
        isOpen: true,
        isCollapsed: false,
      },
      notifications: [],
      isLoading: false,

      setTheme: (theme: Theme) => {
        set({ theme });
        // Apply theme to document
        if (theme.mode === 'dark') {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      },

      toggleTheme: () => {
        const currentTheme = get().theme;
        const newTheme: Theme = {
          ...currentTheme,
          mode: currentTheme.mode === 'light' ? 'dark' : 'light',
        };
        get().setTheme(newTheme);
      },

      toggleSidebar: () => {
        set((state) => ({
          sidebar: {
            ...state.sidebar,
            isOpen: !state.sidebar.isOpen,
          },
        }));
      },

      setSidebarOpen: (isOpen: boolean) => {
        set((state) => ({
          sidebar: {
            ...state.sidebar,
            isOpen,
          },
        }));
      },

      setSidebarCollapsed: (isCollapsed: boolean) => {
        set((state) => ({
          sidebar: {
            ...state.sidebar,
            isCollapsed,
          },
        }));
      },

      addNotification: (toast: Omit<Toast, 'id'>) => {
        const id = Math.random().toString(36).substr(2, 9);
        const newToast: Toast = {
          ...toast,
          id,
          duration: toast.duration || 5000,
        };
        set((state) => ({
          notifications: [...state.notifications, newToast],
        }));

        // Auto-remove notification after duration
        setTimeout(() => {
          get().removeNotification(id);
        }, newToast.duration);
      },

      removeNotification: (id: string) => {
        set((state) => ({
          notifications: state.notifications.filter((toast) => toast.id !== id),
        }));
      },

      clearNotifications: () => {
        set({ notifications: [] });
      },

      setLoading: (isLoading: boolean) => {
        set({ isLoading });
      },
    }),
    {
      name: 'app-storage',
      partialize: (state) => ({
        theme: state.theme,
        sidebar: state.sidebar,
      }),
    }
  )
);
